import axios from 'axios';

/**
 * The Afosto API client.
 *
 * @class ApiClient
 */
export class ApiClient {
  /**
   * The default configuration.
   * @type {Object}
   */
  defaultConfig = {
    baseURL: 'https://api.afosto.io',
    timeout: 60000,
  };

  /**
   * The registered response / request interceptors.
   * @type {Array}
   */
  #interceptors = [];

  /**
   * Initialize the api client.
   * @param {Object} config
   */
  constructor(config = {}) {
    this.client = axios.create({
      ...this.defaultConfig,
      ...(config.client || {}),
    });
    this.client.defaults.headers['Content-Type'] = 'application/json';
  }

  /**
   * Set the bearer token on the authorization header.
   * @param {string} token
   * @public
   */
  setAuthorizationHeader(token) {
    if (token) {
      this.client.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete this.client.defaults.headers.Authorization;
    }
  }

  /**
   * Register a response / request interceptor.
   * @param {function} handler
   * @param {string} type
   * @public
   */
  registerInterceptor(handler, type = 'response') {
    if (type !== 'response' && type !== 'request') {
      throw new Error('Invalid interceptor type. It must be either `request` or `response`.');
    }

    const interceptorId = this.client.interceptors[type].use(
      response => {
        handler(response, null);
        return response;
      },
      error => {
        handler(null, error);
        return Promise.reject(error);
      },
    );

    this.#interceptors.push({ id: interceptorId, handler });
  }

  /**
   * Unregister the provided response / request interceptor.
   * @param {function} handler
   * @param {string} type
   * @public
   */
  unregisterInterceptor(handler, type = 'response') {
    if (type !== 'response' && type !== 'request') {
      throw new Error('Invalid interceptor type. It must be either `request` or `response`.');
    }

    const interceptor = this.#interceptors.find(
      responseHandler => responseHandler.handler === handler,
    );

    if (interceptor) {
      this.client.interceptors[type].eject(interceptor.id);
    }
  }

  /**
   * Execute an API request.
   * @param {Object} options
   * @returns {Promise<any>}
   */
  request(options = {}) {
    const { data } = options;
    const requestConfig = { ...options };

    if (data && !(data instanceof FormData)) {
      requestConfig.data = JSON.stringify(data);
    }

    if (requestConfig.cancel && typeof requestConfig.cancel === 'function') {
      requestConfig.cancelToken = new axios.CancelToken(cancelToken => {
        requestConfig.cancel(cancelToken);
      });
    }

    return this.client.request(requestConfig);
  }
}

export default new ApiClient();
